/**
 * How long to wait between asking again, and how that grows.
 *
 * It was a flat ten seconds, which is right for a conversion that runs
 * for minutes and badly wrong for everything else. Scanning a folder
 * takes about a second, so ten of the eleven seconds it appeared to
 * take were this timer - long enough that the screen looked broken and
 * the button looked ready, which is how one scan became five.
 *
 * Starting short and backing off suits both: a quick job is reported
 * about as soon as it is done, and a long one settles into the same ten
 * second rhythm it always had, about four extra requests later.
 */
const FIRST_WAIT_MS = 1000;
/**
 * How many asks in a row may fail before giving up.
 *
 * Several, because one that does not arrive says nothing about the work
 * - and the two things that make a request fail here, a flaky
 * connection and a request being dropped while another takes its place,
 * both pass. Only a run of them means nobody is going to answer.
 */
const MOST_MISSED = 5;
const LONGEST_WAIT_MS = 10000;
const GROWTH = 2;

/**
 * What the backend says when a pending job is going nowhere.
 *
 * A job nobody can run used to be indistinguishable from a job being
 * worked on: both read "pending", and every poller here waited on both
 * the same way, for ever. The backend now says which it is - see
 * core.serializers.why_nothing_is_running - and this is where that stops
 * being a spinner.
 *
 * Thrown in the same shape as a failed job so callers that already
 * try/catch around these need no new branch: they show errorMessage, and
 * the reason is a sentence written to be shown.
 */
const stalled = (job: any) => ({
  ...job,
  status: 'failed',
  errorMessage: job.stalledReason,
});

/**
 * Waits for a queued job, given something that fetches one.
 *
 * Shared because it was written out at each call site, and the one thing
 * these loops all got wrong - waiting for ever on a job no worker could
 * ever claim - had to be fixed in every copy or in none.
 */
export async function waitForIngestionJob(
  fetchJob: (jobId: string) => Promise<any>,
  jobId: string,
  wait: number = FIRST_WAIT_MS,
  missed: number = 0,
): Promise<any> {
  let job;
  try {
    job = await fetchJob(jobId);
  } catch {
    // A request that did not arrive is not a job that failed. This used
    // to come straight out of here and be reported as "it was not put
    // away" about work that was running perfectly well - the job says
    // what it is doing in its own status, and anything thrown here is
    // the asking, not the answer.
    if (missed + 1 >= MOST_MISSED) {
      throw {
        status: 'failed',
        errorMessage:
          'Lost contact while waiting for this. It may still be running - ' +
          'the row will say.',
      };
    }
    await new Promise((resolve) => setTimeout(resolve, wait));
    return waitForIngestionJob(
      fetchJob,
      jobId,
      Math.min(wait * GROWTH, LONGEST_WAIT_MS),
      missed + 1,
    );
  }
  if (job?.status === 'succeeded') {
    return job;
  }
  if (job?.status === 'failed') {
    throw job;
  }
  if (job?.stalledReason) {
    throw stalled(job);
  }
  await new Promise((resolve) => setTimeout(resolve, wait));
  return waitForIngestionJob(
    fetchJob,
    jobId,
    Math.min(wait * GROWTH, LONGEST_WAIT_MS),
    0,
  );
}

/**
 * Polls an IngestionJob endpoint (see core/ingestion_jobs.py on the
 * backend) until it succeeds or fails. Used inside a custom RTK
 * Query `queryFn` - not a hook, since hooks can't be called there -
 * so it takes `dispatch` and the already-injected job endpoint as
 * plain arguments rather than using `useLazyGetIngestionJobQuery()`
 * the way component-level polling (e.g. MovieStep6.tsx) does.
 *
 * Resolves with the finished job (status "succeeded") or throws it
 * (status "failed"), matching fetchBaseQuery's own resolve/throw
 * contract so callers can just try/catch around it like any other
 * baseQuery call.
 */
export async function pollIngestionJobUntilDone(
  dispatch: any,
  ingestionJobEndpoint: any,
  jobId: string,
): Promise<any> {
  return waitForIngestionJob(
    (id) =>
      dispatch(
        ingestionJobEndpoint.initiate(id, { forceRefetch: true }),
      ).unwrap(),
    jobId,
  );
}
